import { useEffect, useState } from "react";
import { Wallet, Plus, History, AlertTriangle, TrendingUp, HardDrive } from "lucide-react";
import { formatCredits, formatNis, creditsToNis } from "../../lib/credits";
import { api } from "../../lib/api";

/**
 * Unified Credit Center — merges balance card + buy CTA + history.
 * Top-right badge now shows the active STORAGE package + monthly
 * credit cost (replaces the old "ללא חבילה" generic badge).
 */
export default function CreditCenter({
  balance,
  pendingTopups = [],
  companyId,
  onBuy,
  onHistory,
}) {
  const [quota, setQuota] = useState(null);
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    api.get(`/companies/${companyId}/smart-snapshots/quota`)
      .then((r) => { if (!cancelled) setQuota(r.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [companyId]);

  const balanceNum = Number(balance?.credit_balance ?? 0);
  const inDebt = balanceNum < 0;
  const isLow = !inDebt && balanceNum < 50;
  const pendingTotal = pendingTopups.reduce((s, t) => s + (t.credits || 0), 0);
  const nis = creditsToNis(balanceNum);

  // Storage package label (replaces generic package badge per owner request)
  const storageLabel = (() => {
    if (!quota) return null;
    const mb = quota.quota_mb || 200;
    const label = mb >= 1000 ? `${(mb / 1000).toFixed(mb % 1000 === 0 ? 1 : 2)}GB` : `${mb}MB`;
    const extra = Math.max(0, mb - (quota.default_quota_mb || 200));
    const monthlyCredits = extra > 0
      ? Math.round((extra / (quota.tier_mb || 500)) * ((quota.tier_price_ils || 5.9) / 0.6))
      : 0;
    return { label, monthlyCredits };
  })();

  // Visual scheme based on state
  const scheme = inDebt
    ? {
        ring: "border-rose-500/50",
        glow: "from-rose-900/40 via-rose-950/40 to-rose-900/20",
        accent: "text-rose-200",
        bigText: "text-rose-100",
        chip: "bg-rose-500/20 text-rose-200 border-rose-500/40",
        cta: "from-rose-500 to-rose-600 hover:from-rose-400 hover:to-rose-500 shadow-rose-500/30",
        ctaLabel: "שלם חוב עכשיו",
        bar: "from-rose-500/0 via-rose-400/60 to-rose-500/0",
      }
    : isLow
    ? {
        ring: "border-amber-500/40",
        glow: "from-amber-900/30 via-stone-900/30 to-amber-900/20",
        accent: "text-amber-200",
        bigText: "text-amber-100",
        chip: "bg-amber-500/20 text-amber-200 border-amber-500/40",
        cta: "from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 shadow-amber-500/30",
        ctaLabel: "טען קרדיטים",
        bar: "from-amber-400/0 via-amber-300/60 to-amber-400/0",
      }
    : {
        ring: "border-cyan-500/30",
        glow: "from-blue-900/40 via-[#0f1d3d]/40 to-cyan-900/20",
        accent: "text-cyan-200",
        bigText: "text-white",
        chip: "bg-cyan-500/15 text-cyan-200 border-cyan-500/40",
        cta: "from-blue-700 to-cyan-500 hover:from-blue-600 hover:to-cyan-400 shadow-cyan-500/30",
        ctaLabel: "רכוש קרדיטים",
        bar: "from-cyan-400/0 via-cyan-300/60 to-cyan-400/0",
      };

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border ${scheme.ring} bg-gradient-to-br ${scheme.glow} backdrop-blur-md shadow-xl`}
      data-testid="credit-center"
    >
      {/* Animated glow bar */}
      <div className={`absolute -top-px left-0 right-0 h-px bg-gradient-to-r ${scheme.bar}`} />

      <div className="p-5 sm:p-6">
        {/* Top row: label + storage package badge */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-extrabold text-slate-300">
            <Wallet className="h-3.5 w-3.5" />
            {inDebt ? "יתרת חוב" : "יתרת קרדיטים"}
          </div>
          {storageLabel && (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-extrabold border ${scheme.chip}`}
              data-testid="credit-center-storage-pkg"
              title="חבילת אחסון פעילה"
            >
              <HardDrive className="h-2.5 w-2.5" />
              חבילת אחסון: {storageLabel.label}
              {storageLabel.monthlyCredits > 0 && (
                <span className="opacity-80">· {storageLabel.monthlyCredits} קרדיט/חודש</span>
              )}
            </span>
          )}
        </div>

        {/* Big balance display */}
        <div className="flex items-baseline gap-3 flex-wrap">
          <span
            className={`font-heading text-5xl sm:text-6xl font-extrabold tracking-tight ${scheme.bigText} tabular-nums`}
            data-testid="credit-center-balance"
          >
            {inDebt ? "−" : ""}{formatCredits(Math.abs(balanceNum))}
          </span>
          <span className="text-xs font-bold text-slate-400">קרדיטים</span>
        </div>

        {/* NIS equivalent + debt info */}
        <div className="mt-1.5 flex items-center justify-between gap-3 flex-wrap">
          <div className={`text-sm font-bold ${scheme.accent}`} data-testid="credit-center-nis">
            ≈ {inDebt ? "−" : ""}{formatNis(Math.abs(nis))}
          </div>
          {inDebt && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-300" data-testid="credit-center-debt-warning">
              <AlertTriangle className="h-3 w-3" />
              חוב פעיל
            </span>
          )}
          {!inDebt && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-300/80">
              <TrendingUp className="h-3 w-3" />
              חשבון תקין
            </span>
          )}
        </div>

        {/* Pending top-ups */}
        {pendingTotal > 0 && (
          <div className="mt-3 rounded-xl bg-amber-500/15 border border-amber-400/40 px-3 py-2 text-[11px] text-amber-200" data-testid="credit-center-pending">
            ⏳ ממתין לאישור: <strong className="text-white">+{pendingTotal}</strong> קרדיטים
          </div>
        )}

        {/* CTA row */}
        <div className="mt-5 grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={onBuy}
            className={`col-span-2 inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-l ${scheme.cta} text-white font-extrabold text-sm shadow-lg transition active:scale-[0.98]`}
            data-testid="credit-center-buy"
          >
            <Plus className="h-4 w-4" />
            {scheme.ctaLabel}
          </button>
          <button
            type="button"
            onClick={onHistory}
            className="inline-flex items-center justify-center gap-1.5 py-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-slate-200 font-bold text-xs transition"
            data-testid="credit-center-history"
          >
            <History className="h-4 w-4" />
            היסטוריה
          </button>
        </div>
      </div>
    </div>
  );
}
